/** Shared booking-form visibility rules: one source of truth for the booking
 *  form, Dock Manager peek, print page and email-template warnings. */
import { useEffect, useState } from "react";
import { TABS } from "@/components/booking-form/registry/tabs";
import { FIELDS } from "@/components/booking-form/registry/fields";
import { applyTabOverrides, applyFieldOverrides, listFieldOverrides } from "@/lib/api/fieldOverrides";

export function buildFormVisibility(fieldOverrides) {
  const ov = fieldOverrides || [];
  const tabs = new Set(applyTabOverrides(TABS, ov).map((t) => t.key));
  const fields = new Map(applyFieldOverrides(FIELDS, ov).map((f) => [f.key, f]));
  const fieldTab = new Map(FIELDS.map((f) => [f.key, ov.find((o) => o.scope === "field" && o.field_key === f.key)?.tab_key || f.tab]));
  const hiddenSlots = new Set(ov.filter((o) => o.scope === "slot" && o.is_visible === false).map((o) => o.field_key));
  // Only tenant-renamed labels replace a screen's own wording.
  const renamed = (scope) => new Map(ov.filter((o) => o.scope === scope && o.label).map((o) => [o.field_key, o.label]));
  const tabRenames = renamed("tab");
  const fieldRenames = renamed("field");
  return {
    loaded: true,
    tabVisible:   (k) => tabs.has(k),
    tabLabel:     (k, fb) => tabRenames.get(k) || fb,
    slotVisible:  (k) => !hiddenSlots.has(k),
    fieldVisible: (k) => fields.has(k) && tabs.has(fields.get(k).tab),
    fieldLabel:   (k, fb) => fieldRenames.get(k) || fb,
    // Date (YYYY-MM-DD) a field — or its tab — was last switched off; null if visible.
    hiddenSince:  (k) => {
      const hid = (scope, key) => ov.find((o) => o.scope === scope && o.field_key === key && o.is_visible === false);
      const row = hid("field", k) || hid("tab", fieldTab.get(k));
      return row?.updated_at ? row.updated_at.slice(0, 10) : null;
    },
  };
}

export const ALL_VISIBLE = { ...buildFormVisibility([]), loaded: false };

const cache = new Map();
const TTL_MS = 60_000; // Field Designer edits show up within a minute

/** Per-tenant visibility, cached per session. Everything visible until loaded. */
export function useFormVisibility(clientId) {
  const [vis, setVis] = useState(() => cache.get(clientId)?.value || ALL_VISIBLE);
  useEffect(() => {
    if (!clientId) { setVis(ALL_VISIBLE); return; }
    let entry = cache.get(clientId);
    if (!entry || Date.now() - entry.at > TTL_MS) {
      entry = { at: Date.now(), promise: listFieldOverrides(clientId).then(({ data }) => {
        entry.value = buildFormVisibility(data || []);
        return entry.value;
      }) };
      cache.set(clientId, entry);
    }
    let cancelled = false;
    entry.promise.then((v) => { if (!cancelled) setVis(v); });
    return () => { cancelled = true; };
  }, [clientId]);
  return vis;
}

// Email placeholders that come from booking-form fields / slots.
export const PLACEHOLDER_SOURCES = {
  sender_name: { f: "sender_name" }, sender_company: { f: "sender_company" },
  receiver_name: { f: "receiver_name" }, receiver_company: { f: "receiver_company" },
  company_name: { f: "sender_company" }, goods_description: { f: "goods_description" },
  external_ref: { f: "external_ref" }, internal_ref: { f: "internal_ref" },
  po_number: { f: "po_number" }, manifest_number: { f: "manifest_number" },
  vehicle_rego: { f: "rego_prime_mover" }, vehicle_type: { s: "vehicle.picklists" },
  driver_first_name: { s: "driver.picker" }, driver_last_name: { s: "driver.picker" },
  driver_phone: { s: "driver.picker" }, driver_email: { s: "driver.picker" },
};

/** Placeholders used in `text` whose source field/slot the tenant has switched off. */
export function hiddenPlaceholders(text, vis) {
  const used = new Set([...(text || "").matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g)].map((m) => m[1]));
  return [...used].filter((k) => {
    const src = PLACEHOLDER_SOURCES[k];
    return src && (src.f ? !vis.fieldVisible(src.f) : !vis.slotVisible(src.s));
  });
}
